import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
// Constants
import { MULTIPLE_IMAGES, NEW_ROOM } from 'src/constants';
// Stores
import { home_data, user_data } from 'src/stores/account/user';
import {
	discovered_settings,
	discovered_statuses,
	devices_status,
	devices_settings,
	discovered_info,
} from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
import { network } from 'src/stores/session/native-bridge';
import { app, os } from 'src/stores/session/native-bridge';
// Helpers
import {
	get_mode,
	isCover,
	isDoorWindow,
	isFlood,
	isMotion,
	isShellyPresence,
	isSmoke,
	isTemperatureHumidity,
	isZendure,
	isWallDisplay,
	load_image_from,
	parseAlarmDeviceRr,
	real_id,
	shellyId16,
	is_gen_2,
} from 'src/helpers/interface_helpers';

import type { Device, Devices, MissingDevicesGroup } from 'src/types/Device';
import type { GetImagesRequest } from 'src/types/requests/process/get_images';

import { HiddenDevicesRoomID, SharedRecentlyDevicesRoomID } from 'src/helpers/service_rooms_helpers';

import { capFirstChar } from 'src/helpers/utilities';
import { dashboards } from 'src/stores/features/dashboards';
import { getPeripheralDigitalInputDevices } from 'src/helpers/addons/addon_device_helpers';
import { getComponentByDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { bthomeDeviceToModel } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { getXT1Services } from 'src/helpers/xt1_helpers/template_extract_helpers';
// Services
import { open_external_link, open_loqed_linking_ios } from 'src/services/native_container_requests_ios';
import { open_external_link_android } from 'src/services/native_container_requests_android';
import { postReq } from 'src/services/http';
import { scenes } from 'src/stores/lists/scenes';

//Design system components
import { toast } from '@Shelly/shelly-design-system';
import { upload_image } from 'src/helpers/upload_image';
import { convertSecondsToDDHHMMSS } from '@Shelly/shelly-design-system/helpers/component-utils';

// Types
import type { ProcessDeviceDataRequest } from 'src/types/requests/process/process_device_data';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import type { BulkUpdateGroupResponse, SaveGroupResponse } from 'src/types/responses/Group';
import type { DashboardDeleteResponse, DashboardUpdateResponse } from 'src/types/Dashboard/Responses';
import { isTestUser } from 'src/helpers/user_helpers';
import { validateBaddPayload } from 'src/helpers/validators';
import type { IAlarmZone } from '../AlarmZones/AlarmWizard/alarm_object';
import type { wizardItem } from 'src/types/WizardItemType';
import { showNewLayout } from 'src/stores/toDelete/new_layout';

// import type { Room } from 'src/types/Room';
let _format = get(format);

// Device category definitions
export const DEVICE_CATEGORY_ICONS = {
	// Module family
	module: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-microchip',
			id: 'icon-microchip',
		},
		{ local: false, isIcon: true, icon: 'fat fa-server', id: 'icon-server' },
		{ local: false, isIcon: true, icon: 'fat fa-sliders', id: 'icon-sliders' },
		{ local: false, isIcon: true, icon: 'fat fa-plug', id: 'icon-plug' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-network-wired',
			id: 'icon-network-wired',
		},
	],

	// Input readers family
	inputs_reader: [
		{ local: false, isIcon: true, icon: 'fat fa-plug', id: 'icon-plug' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-power-off',
			id: 'icon-power-off',
		},
		{ local: false, isIcon: true, icon: 'fat fa-bolt', id: 'icon-bolt' },
		{ local: false, isIcon: true, icon: 'fat fa-server', id: 'icon-server' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-toggle-off',
			id: 'icon-toggle-button',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-hand-pointer',
			id: 'icon-pointer',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-circle-dot',
			id: 'icon-circle-dot',
		},
	],

	// Temperature family
	temperature: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-thermometer-half',
			id: 'icon-thermometer-half',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-temperature-high',
			id: 'icon-temperature-high',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-droplet-percent',
			id: 'icon-droplet-percent',
		},
	],

	// Flood and water detection family
	flood: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-house-flood-water',
			id: 'icon-flood',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-house-flood-water-circle-arrow-right',
			id: 'icon-flood-arrow',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-droplet',
			id: 'icon-water-drop',
		},
	],

	// Smoke detection family
	smoke: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-fire-smoke',
			id: 'icon-fire-smoke',
		},
		{ local: false, isIcon: true, icon: 'fat fa-smoke', id: 'icon-smoke' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-sensor-fire',
			id: 'icon-sensor-fire',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-sensor-cloud',
			id: 'icon-sensor-cloud',
		},
	],

	// Gateway family
	gateway: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-usb-drive',
			id: 'icon-usb-drive',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-bluetooth',
			id: 'icon-bluetooth',
		},
		{ local: false, isIcon: true, icon: 'fat fa-wifi', id: 'icon-wifi' },
	],

	// Display family
	display: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-tablet-screen-button',
			id: 'icon-tablet',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-mobile-screen',
			id: 'icon-mobile-screen',
		},
		{ local: false, isIcon: true, icon: 'fat fa-display', id: 'icon-display' },
	],

	// Thermostat family
	thermostat: [
		{ local: false, isIcon: true, icon: 'fat fa-heat', id: 'icon-heat' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-temperature-three-quarters',
			id: 'icon-temperature-three-quarters',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-temperature-sun',
			id: 'icon-temperature-sun',
		},
	],

	// Relay family
	relay: [
		{ local: false, isIcon: true, icon: 'fat fa-plug', id: 'icon-plug' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-power-off',
			id: 'icon-power-off',
		},
		{ local: false, isIcon: true, icon: 'fat fa-bolt', id: 'icon-bolt' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-lightbulb',
			id: 'icon-lightbulb',
		},
		{ local: false, isIcon: true, icon: 'fat fa-server', id: 'icon-server' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-garage-open',
			id: 'garage-open',
		},
		{ local: false, isIcon: true, icon: 'fat fa-heat', id: 'heat' },
		{ local: false, isIcon: true, icon: 'fat fa-blinds', id: 'blinds' },
		{ local: false, isIcon: true, icon: 'fat fa-dryer-heat', id: 'dryer-heat' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-charging-station',
			id: 'charging-station',
		},
		{ local: false, isIcon: true, icon: 'fat fa-tv-retro', id: 'tv-retro' },
		{ local: false, isIcon: true, icon: 'fat fa-oven', id: 'oven' },
		{ local: false, isIcon: true, icon: 'fat fa-fan', id: 'fan' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-refrigerator',
			id: 'refrigerator',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-water-ladder',
			id: 'water-ladder',
		},
		{ local: false, isIcon: true, icon: 'fat fa-microchip', id: 'microchip' },
	],

	// Power meter family
	power_meter: [
		{ local: false, isIcon: true, icon: 'fat fa-plug-circle-bolt', id: 'icon-plug-circle-bolt' },
		{ local: false, isIcon: true, icon: 'fat fa-meter-bolt', id: 'icon-meter-bolt' },
		{ local: false, isIcon: true, icon: 'fat fa-battery-bolt', id: 'icon-battery-bolt' },
		{ local: false, isIcon: true, icon: 'fat fa-chart-line', id: 'icon-chart-linet' },
		{ local: false, isIcon: true, icon: 'fat fa-chart-area', id: 'icon-chart-area' },
		{ local: false, isIcon: true, icon: 'fat fa-chart-fft', id: 'icon-chart-fft' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-power-off',
			id: 'icon-power-off',
		},
		{ local: false, isIcon: true, icon: 'fat fa-bolt', id: 'icon-bolt' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-lightbulb',
			id: 'icon-lightbulb',
		},
		{ local: false, isIcon: true, icon: 'fat fa-server', id: 'icon-server' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-garage-open',
			id: 'garage-open',
		},
		{ local: false, isIcon: true, icon: 'fat fa-heat', id: 'heat' },
		{ local: false, isIcon: true, icon: 'fat fa-blinds', id: 'blinds' },
		{ local: false, isIcon: true, icon: 'fat fa-dryer-heat', id: 'dryer-heat' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-charging-station',
			id: 'charging-station',
		},
		{ local: false, isIcon: true, icon: 'fat fa-tv-retro', id: 'tv-retro' },
		{ local: false, isIcon: true, icon: 'fat fa-oven', id: 'oven' },
		{ local: false, isIcon: true, icon: 'fat fa-fan', id: 'fan' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-refrigerator',
			id: 'refrigerator',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-water-ladder',
			id: 'water-ladder',
		},
		{ local: false, isIcon: true, icon: 'fat fa-microchip', id: 'microchip' },
	],

	// Remote control family
	remote_control: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-gamepad',
			id: 'icon-gamepad',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-tower-control',
			id: 'icon-tower-control',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-joystick',
			id: 'icon-joystick',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-slider',
			id: 'icon-slider',
		},
	],

	// Light family
	light: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-lightbulb',
			id: 'icon-lightbulb',
		},
		{ local: false, isIcon: true, icon: 'fat fa-sun', id: 'icon-sun' },
		{
			local: false,
			isIcon: true,
			icon: 'fa-light fa-sun-bright',
			id: 'icon-circle-half-stroke',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-lightbulb-on',
			id: 'icon-lightbulb-on',
		},
	],

	// Door/window family
	door_window: [
		{ local: false, isIcon: true, icon: 'fat fa-door-open', id: 'icon-door' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-door-closed',
			id: 'icon-door-closed',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-window-frame',
			id: 'icon-window-frame',
		},
		{ local: false, isIcon: true, icon: 'fat fa-garage', id: 'icon-garage' },
	],

	// Shutter family
	roller: [
		{ local: false, isIcon: true, icon: 'fat fa-blinds', id: 'icon-blinds' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-blinds-raised',
			id: 'icon-blinds-raised',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-blinds-open',
			id: 'icon-blinds-open',
		},
	],

	// Energy meter family
	emeter: [
		{ local: false, isIcon: true, icon: 'fat fa-bolt', id: 'icon-bolt' },
		{ local: false, isIcon: true, icon: 'fat fa-gauge', id: 'icon-gauge' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-chart-line',
			id: 'icon-chart-line',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-gauge-circle-bolt',
			id: 'icon-gauge-circle-bolt',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-garage-open',
			id: 'garage-open',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-lightbulb',
			id: 'icon-lightbulb',
		},
		{ local: false, isIcon: true, icon: 'fat fa-heat', id: 'heat' },
		{ local: false, isIcon: true, icon: 'fat fa-dryer-heat', id: 'dryer-heat' },
		{ local: false, isIcon: true, icon: 'fat fa-fan', id: 'fan' },
		{ local: false, isIcon: true, icon: 'fat fa-oven', id: 'oven' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-refrigerator',
			id: 'refrigerator',
		},
		{ local: false, isIcon: true, icon: 'fat fa-blinds', id: 'blinds' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-charging-station',
			id: 'charging-station',
		},
		{ local: false, isIcon: true, icon: 'fat fa-tv-retro', id: 'tv-retro' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-water-ladder',
			id: 'water-ladder',
		},
		{ local: false, isIcon: true, icon: 'fat fa-microchip', id: 'microchip' },
	],

	// Sensor family
	sensor: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-person-walking',
			id: 'icon-walking',
		},
		{ local: false, isIcon: true, icon: 'fat fa-eye', id: 'icon-eye' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-sensor-on',
			id: 'icon-sensor-on',
		},
		{ local: false, isIcon: true, icon: 'fat fa-radar', id: 'icon-radar' },
	],

	breaker: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-bolt-lightning',
			id: 'icon-bolt-lightning',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-circle-bolt',
			id: 'icon-circle-bolt',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-shield-check',
			id: 'icon-shield-check',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-plug-circle-bolt',
			id: 'icon-plug-circle-bolt',
		},
	],

	// Valve family
	valve: [
		{ local: false, isIcon: true, icon: 'fat fa-faucet', id: 'icon-faucet' },
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-faucet-drip',
			id: 'icon-faucet-drip',
		},
		{ local: false, isIcon: true, icon: 'fat fa-shower', id: 'icon-shower' },
	],

	// Zendure family
	zendure: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-solar-panel',
			id: 'icon-solar-panel',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-house-day',
			id: 'icon-house-day',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-car-battery',
			id: 'icon-car-battery',
		},
	],

	// Camera family
	camera: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-camera-cctv',
			id: 'icon-camera-cctv',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-camera-security',
			id: 'icon-camera-security',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-camera-web',
			id: 'icon-camera-web',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-video',
			id: 'icon-video',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-webcam',
			id: 'icon-webcam',
		},
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-eye',
			id: 'icon-eye',
		},
	],

	// Default family
	default: [
		{
			local: false,
			isIcon: true,
			icon: 'fat fa-question-circle',
			id: 'icon-question',
		},
	],
};

export const getImagesByType = (type, deviceType, device = null) => {
	let currentType = type;
	let image_urls = [];

	switch (currentType) {
		case 'device':
			// Create base filepath for device images
			const baseFilePath = `images/device_images/${
				isXT1Module(deviceType) && device?.service_type ? device.service_type : deviceType
			}`;

			// Initialize with empty array
			image_urls = [];

			// First add color variants from MULTIPLE_IMAGES
			let hasDefaultImage = false;
			MULTIPLE_IMAGES.forEach((image) => {
				if (image.includes(deviceType)) {
					// Add variant images
					image_urls.push({
						local: true,
						filepath: `images/device_images/${image}.png`,
					});

					// Check if this is the default image
					if (image === deviceType) {
						hasDefaultImage = true;
					}
				}
			});

			// Add default image only if it wasn't already added through MULTIPLE_IMAGES
			if (!hasDefaultImage) {
				image_urls.push({
					local: true,
					filepath: `${baseFilePath}.png`,
					id: 'default-image',
				});
			}

			// Remove any duplicates that might still exist
			image_urls = [...new Set(image_urls.map((o) => JSON.stringify(o)))].map((s) =>
				JSON.parse(s),
			);

			// Get device family for icon categorization
			let deviceFamily = device?.category || device?.family || 'default';

			if (isXT1Module(deviceType)) {
				deviceFamily = 'module';
			}

			if (isZendure(deviceType)) {
				deviceFamily = 'zendure';
			} else if (isWallDisplay(deviceType)) {
				deviceFamily = 'display';
			} else if (isCover(deviceType)) {
				const mode = get_mode(
					device,
					devices_status.getByRealId(device.id),
					devices_settings.getByRealId(device.id),
				);
				deviceFamily = mode === 'roller' ? 'roller' : 'relay';
			}

			if (deviceFamily === 'sensor') {
				if (isTemperatureHumidity(deviceType)) {
					deviceFamily = 'temperature';
				} else if (isFlood(deviceType)) {
					deviceFamily = 'flood';
				} else if (isDoorWindow(deviceType)) {
					deviceFamily = 'door_window';
				} else if (isSmoke(deviceType)) {
					deviceFamily = 'smoke';
				} else if (isMotion(deviceType)) {
					deviceFamily = 'sensor';
				}
			}

			// Add icons based on device family
			if (DEVICE_CATEGORY_ICONS[deviceFamily]) {
				image_urls.push(...DEVICE_CATEGORY_ICONS[deviceFamily]);
			} else {
				// Fallback to default icons if family not found
				image_urls.push(...DEVICE_CATEGORY_ICONS.default);
			}

			// ADD DEVICE'S CUSTOM ICON IF IT EXISTS
			if (device?.customIcon && device.customIcon !== '') {
				// Check if this custom icon is already in the standard icons
				const iconExists = image_urls.some(
					(img) => img.isIcon && img.icon === device.customIcon,
				);

				if (!iconExists) {
					// Add the custom icon to the list
					const customIconObj = {
						id: `device-custom-icon`,
						icon: device.customIcon,
						isIcon: true,
						local: false,
						type: 'imgType',
						isCustom: true,
					};

					// Find the first icon in the list to insert before it
					const firstIconIndex = image_urls.findIndex((img) => img.isIcon);

					if (firstIconIndex !== -1) {
						// Insert at the beginning of the icons (before the first icon)
						image_urls.splice(firstIconIndex, 0, customIconObj);
					} else {
						// If no icons found, add it at the end
						image_urls.push(customIconObj);
					}
				}
			}

			break;

		case 'home':
			image_urls = [{ local: true, filepath: `images/home_def/home_def.jpg` }];
			break;

		case 'temp_scheme':
			// Nothing to do for temp scheme
			break;

		default:
			// Default case for rooms, etc.
			image_urls = [
				{ local: true, filepath: `images/room_def/bedroom_img_def_m.jpg` },
				{ local: true, filepath: `images/room_def/bedroom_img_def_2_m.jpg` },
				{ local: true, filepath: `images/room_def/kitchen_img_def_m.jpg` },
				{
					local: true,
					filepath: `images/room_def/living_room_img_def_2_m.jpg`,
				},
				{ local: true, filepath: `images/room_def/living_room_img_def_m.jpg` },
			];
			break;
	}

	return image_urls;
};

// Save a custom icon for a device type
export const saveCustomIconForDevice = (icon, deviceType) => {
	return false;
};

// Load all custom icons for a device type
export const loadCustomIconsForDevice = (deviceType) => {
	return [];
};

// Icon helpers
export function generateThermostatIcon(item, iconWeight = 'fa-light') {
	const type = item._meta?.type;
	return type === 'cool' ? `${iconWeight} fa-temperature-snow` : `${iconWeight} fa-temperature-sun`;
}

export function generateAlarmIcon(item) {
	const isEnabled = item._enabled;
	return isEnabled ? 'fa-solid fa-shield' : 'fa-sharp fa-solid fa-shield-slash';
}

export function createThermostatDescription(item: any, status: any): string {
	try {
		let value = status?.int_temp || status?.base_temp || item?.deftemp;
		const units = item?.isinfh ? 'F' : 'C';
		const type = item?._meta?.type;
		if (!value || +value < +item?._meta[units][type].min || +value > +item?._meta[units][type].max) {
			value = +item?.deftemp || 18;
		}
		const formattedTemp = `${value} °${units}`;
		return `${capFirstChar(_format(`label_${type}ing`))} ${formattedTemp}`;
	} catch (error) {
		console.log('error', error);
		return `${capFirstChar(_format(`alert_danger_error_heading`))}`;
	}
}

export function createAlarmDescription(item) {
	const isEnabled = item._enabled;
	return isEnabled
		? `${capFirstChar(
				_format('label_list_description_alarm_armed', {
					default: 'armed',
				}),
			)}`
		: `${capFirstChar(
				_format('label_list_description_alarm_disarmed', {
					default: 'disarmed',
				}),
			)}`;
}

export const getImages = async (type) => {
	console.log('get(network)', get(network));
	let img_arr = [];
	try {
		let res: GetImagesRequest = await postReq(
			'/user_files/image_list',
			{
				type: type,
			},
			12000,
		);

		if (res.isok) {
			img_arr = res.data.files.map(({ thumb, filepath }) => ({
				local: false,
				filepath: `assets/user_images/${thumb || filepath}`,
			}));
		}

		return img_arr.reverse();
	} catch (error) {
		console.log('error', error);
		return img_arr.reverse();
	}
};

export const uploadImage = async (event, type) => {
	const res = await upload_image(event, type);
	let images = [];
	if (res.isok) {
		images = await getImages(type);
		return images;
	} else {
		toast.open({ type: 'info', title: res.err });
		return res;
	}
};

export const mapImgArray = (arr, imgType, selectedDeviceImage, selectedIcon = '') => {
	if (!arr || !arr.length) return [];
	let selectedIndex = -1;

	let mappedArray = arr.map((img, i) => {
		// Check if this is an icon
		const isIconItem = !!img.isIcon;

		// Determine if selected
		let isSelected = false;
		if (isIconItem && img.icon === selectedIcon) {
			isSelected = true;
		} else if (!isIconItem && img.filepath === selectedDeviceImage) {
			isSelected = true;
		}

		return {
			...img,
			filepath: img.filepath || '',
			image: isIconItem
				? ''
				: img.thumb
					? load_image_from(img.thumb)
					: load_image_from(img.filepath),
			id: img.id || i,
			selected: isSelected,
			type: imgType,
			isIcon: isIconItem,
			icon: img.icon || '',
		};
	});

	// Move selected item to front if found
	if (selectedIndex >= 0) {
		const selectedImg = mappedArray[selectedIndex];
		mappedArray.splice(selectedIndex, 1);
		mappedArray = [selectedImg, ...mappedArray];
	}

	return mappedArray;
};

// Request helpers
const lists = {
	device: devices,
	room: rooms,
	group: groups,
	scene: scenes,
	alarms: alarms,
	temp_schemes: temp_schemes,
};

export const requestAddDevice = async (item) => {
	if (import.meta.env.DEV) console.log('%cPassed device to add', 'color:red', item);

	const add_list = {};
	let requestUrl = 'device/badd';
	let requestParams = undefined;

	const isGBLE = item.gen === 'GBLE';
	const isGen1 = item.gen == 1;
	let ni: number = 0,
		no: number = 0;
	let xt1Services = [];

	if (item.type === 'S3MX-0A') {
		const discoveredSettings = get(discovered_settings)[item.id];

		for (let key in discoveredSettings) {
			if (key.startsWith('switch')) {
				no++;
			}
			if (key.startsWith('input')) {
				ni++;
			}
		}
		item.channels_count = Math.max(ni, no) || 1;
	}

	if (isXT1Module(item.type)) {
		const discoveredInfo = get(discovered_info)[item.id];
		xt1Services = getXT1Services(discoveredInfo);
		item.channels_count = xt1Services.length || 1;
	}

	for (let i = 0; i < item.channels_count; i++) {
		// copy main device data object
		const item_clone = Object.assign({}, item);

		if (import.meta.env.DEV) {
			console.log('%c "item_clone" the colone of the main channel', 'color:yellow', item_clone);
		}

		if (item_clone.id.includes('-')) {
			item_clone.id = item.id.split('-')[1];
		}

		if (isGen1 || isWallDisplay(item_clone.type)) {
			item_clone.id = item_clone.id.toLowerCase();
		}

		if (isGBLE) {
			// if (isIncluded) {
			// 	const key_enc = typeof item_clone?.settings?.encryptionKey !== 'undefined' ? item_clone.settings.encryptionKey : item_clone.key_enc;
			// 	requestUrl = 'device/write_ek';//already encrypted devices
			// 	requestParams = { id: item_clone.id, key_enc };
			// } else {
			requestUrl = 'device/addble'; //changing endpoint for GBLE devices}
			// }
		}

		// XMOD device(channel) mode is determined by input/output count
		if (item.type === 'S3MX-0A') {
			item_clone.mode = getModuleDeviceMode(ni, no);
			item_clone.category = 'module';
			no--;
			ni--;
		}

		if (['SPDC-0D5PE16EU'].includes(item.type)) {
			// using RGBWW channel mapping to get the interface channel's mode
			const discoveredStatus = get(discovered_statuses)[item.id];
			const discoveredSettings = get(discovered_settings)[item.id];
			item_clone.mode = discoveredStatus?.sys?.ch[i]?.split(':')[0];
			item_clone.rgbww_profile = discoveredSettings?.profile;
		}

		if (isXT1Module(item.type) && xt1Services.length > 0) {
			const service = xt1Services[i] || xt1Services[0];
			if (service) {
				item_clone.service_type = service.type;
				item_clone.category = service.category;
				item_clone.mode = service.mode;
				item_clone.image = service.image;
				item_clone.component = service.component;
			}
		}

		item_clone.channel = i;

		if (typeof requestParams === 'undefined') {
			const same_room_devices = Object.values(get(devices)).filter(
				(v: any) => v.room_id == item.room_id,
			);

			let max_pos = 0;

			if (same_room_devices.length > 0) {
				const max_pos_device: any = same_room_devices.reduce((prev: any, current: any) =>
					prev.position > current.position ? prev : current,
				);
				max_pos = max_pos_device.position + 1;
			}

			item_clone.position = max_pos + i;

			if (i > 0) {
				item_clone.id = `${item_clone.id}_${i}`;
				item_clone.name = `${i + 1}) ${item.name}`;
			}
			add_list[item_clone.id] = item_clone;
		}
	}

	for (const deviceId in add_list) {
		const validation = validateBaddPayload(add_list[deviceId]);
		if (!validation.valid) {
			console.error(
				`[requestAddDevice] Invalid device payload for ${deviceId}. ` +
					`Missing fields: ${validation.missingFields.join(', ')}. ` +
					`Device type: ${add_list[deviceId].type || 'unknown'}. ` +
					`Full payload:`,
				add_list[deviceId],
			);

			toast.open({
				type: 'danger',
				title: _format('failed_to_add', {
					default: 'Failed to add the device, please try again.',
				}),
				text: _format('cloud_connect_owned_err_toast_text', {
					values: { deviceId },
					default:
						'If you believe this is a mistake, please contact our support and provide the device ID: {deviceId}',
				}),
			});

			return {
				isok: false,
				err: new Error(`Missing required fields: ${validation.missingFields.join(', ')}`),
			};
		}
	}

	requestParams = { devices: JSON.stringify(add_list) };

	if (item?.bundle) {
		requestParams = { ...requestParams, bundle: 1 };
	}

	if (item?.bundle) {
		requestParams = { ...requestParams, bundle: 1 };
	}

	let res: CloudApiResponse;
	try {
		res = await postReq(requestUrl, requestParams, 20000);
		if (Object.keys(res.data?.errors).length > 0) {
			res.isok = false;
			throw new Error('Error could not associate device with account!');
		}
		if (res.isok) {
			for (const key in add_list) {
				if (Object.prototype.hasOwnProperty.call(add_list, key)) {
					let _item = add_list[key];
					_item.cloud_online = true;
					requestEditDevice(_item);
				}
			}

			return res;
		}
	} catch (error) {
		console.error(error);
		return { isok: false, err: error, ...res }; //passing the res object because it's needed in the calling function
	}
};

export const requestEditDevice = async (item) => {
	const res = await postReq(`interface/device/save`, {
		id: item.id,
		data: JSON.stringify(item),
	});

	if (res?.isok) {
		devices.update(item);
	} else {
		console.error('could not add shared device');
	}
	return res;
};

export const requestEditSharedDevice = async (item) => {
	const res = await postReq(`interface/device/edit_shared`, {
		id: item.id,
		params: JSON.stringify(item),
	});
	if (res.isok) {
		devices.update(item);
	} else {
		console.error('could not add shared device');
	}
	return res;
};

export const requestReorderDevice = async (
	type: 'device' | 'room' | 'group' | 'scene' | 'alarm' | 'temp_scheme',
	editedList,
) => {
	const keys = {
		device: 'devices_data',
		room: 'rooms_data',
		group: 'groups_data',
		scene: 'scenes_data',
	};

	let url =
		type === 'scene' || type === 'alarm' || type === 'temp_scheme'
			? `${type}/bulk_update`
			: `interface/${type}/bulk_update`;
	let body: Record<string, string> = {
		[keys[type]]: JSON.stringify(type !== 'scene' ? editedList : Object.values(editedList)),
	};

	if (type === 'scene') {
		body.reorder = '1';
	}

	const res = await postReq(url, body);

	if (res.isok) {
		if (type === 'scene') {
			// scenes update function takes in an object of scenes by ids
			lists[type].update(editedList);
		} else {
			Object.values(editedList).map((e) => lists[type].update(e));
		}
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
};

export const requestSaveRoom = async (room, isEdit) => {
	let payload = isEdit ? { id: room.id, data: JSON.stringify(room) } : { data: JSON.stringify(room) };

	const res: ProcessDeviceDataRequest = await postReq('interface/room/save', payload);
	if (res.isok) {
		if (!room?.id) {
			// When adding a new item, set the temporary prop `new` to show the pulse animation
			room = { ...room, new: true };
		}
		room.id = res.data.room_id;
		rooms.update(room);
	} else {
		// show error
	}
};

export const requestDeleteRoom = async (roomID, destinationRoom) => {
	return await postReq('interface/room/delete', {
		id: roomID,
		dest: destinationRoom,
	});
};

/**
 * Move every device that lives in `roomID` to `destinationRoomID`.
 *
 * Used by the Delete-Room flow: the cloud `interface/room/delete` endpoint does
 * not reliably re-home devices via its `dest` parameter, so we explicitly
 * `bulk_update` the affected devices first (mirroring the proven pattern in
 * `BatchTransferToAnotherRoomModal.svelte`) and also patch the local `devices`
 * store so the UI updates immediately, without waiting for a refresh.
 *
 * Pass `HiddenDevicesRoomID` (-10) to move devices to "Hidden devices" (the
 * default behavior when the user does not pick a transfer room).
 *
 * Returns `{ isok: true }` when there is nothing to do or on success, and the
 * raw failed response otherwise so callers can short-circuit before deleting
 * the room.
 */
export const transferDevicesToRoom = async (
	roomID: number,
	destinationRoomID: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const devicesInRoom = Object.values(get(devices)).filter(
		(d: Device) => d.room_id === roomID,
	) as Device[];

	if (devicesInRoom.length === 0) {
		return { isok: true };
	}

	const updatedDevices = devicesInRoom.reduce(
		(acc, device) => {
			acc[device.id] = { ...device, room_id: destinationRoomID };
			return acc;
		},
		{} as { [key: string]: Device },
	);

	const res = await postReq('interface/device/bulk_update', {
		devices_data: JSON.stringify(updatedDevices),
	});

	if (res.isok) {
		devices.massUpdate(updatedDevices);
	}

	return res;
};

/**
 * Create or edit group
 * @param group edited group
 * @param isEdit group id (if you have group id it means you are editing the group, otherwise you don't have id yet)
 */
export const requestSaveGroup = async (group, isEdit) => {
	if (isEdit) {
		const newGroup = (id, newObj) => {
			return {
				groups_data: JSON.stringify({
					[id]: newObj,
				}),
			};
		};

		const bulkUpdateGroup = async (id, updatedGroup) => {
			return await postReq('/interface/group/bulk_update', newGroup(id, updatedGroup));
		};

		const res: BulkUpdateGroupResponse = await bulkUpdateGroup(group.id, group);

		if (res.isok) {
			groups.update(res.data.groups_data[group.id]);
		} else {
			// show error
		}
	} else {
		const res: SaveGroupResponse = await postReq('interface/group/save', {
			data: JSON.stringify(group),
		});

		if (res.isok) {
			// When adding a new item, set the temporary prop `new` to show the pulse animation
			groups.update({ ...res.data.group_data, new: true });
			return res;
		} else {
			// show error
		}
	}
};

export const requestDeleteGroup = async (groupID) => {
	return await postReq('interface/group/delete', { id: groupID });
};

export const requestDeleteScene = async (sceneID) => {
	return await postReq('/scene/delete', { id: sceneID });
};

export const requestDeleteThermostat = async (thermostatID) => {
	return await postReq('/scene/delete', { id: thermostatID });
};

export const requestDeleteRoomThermostats = async (ids: string[]) => {
	return await postReq('/scene/bulk_delete', { ids: JSON.stringify(ids) });
};

// Room list helpers

export const getDeviceRoomList = () => {
	const homeDataValue = get(home_data);
	const allRooms = get(rooms);

	let roomsList = [];

	const hiddenDevices = {
		name: get(format)('list_edit_device_room_hidden_devices', {
			default: 'Hidden Devices',
		}),
		filepath:
			typeof homeDataValue?.general?.image !== 'undefined'
				? homeDataValue.general.image
				: 'images/home_def/home_def.jpg',
		id: -10,
		main_sensor: false,
	};

	const newRoom = {
		name: NEW_ROOM,
		icon: 'fa-solid fa-plus',
		id: -30,
		main_sensor: false,
	};

	roomsList = Object.values(allRooms)
		.sort((x: any, y: any) => {
			return x.position - y.position;
		})
		.map(({ name, image, backgroundColor, id, main_sensor }) => ({
			name,
			filepath: image,
			backgroundColor,
			id,
			main_sensor,
		}));

	return [newRoom, ...roomsList, hiddenDevices];
};

export const getThermostatRoomList = () => {
	const allRooms = get(rooms);

	let roomsList = [];

	roomsList = Object.values(allRooms)
		.sort((x: any, y: any) => {
			return x.position - y.position;
		})
		.map(({ name, image, backgroundColor, id }) => ({
			name,
			filepath: image,
			backgroundColor,
			id,
		}));

	return roomsList;
};

export const getGroupRoomList = () => {
	const allRooms = get(rooms);

	return Object.values(allRooms)
		.sort(sortByPosition)
		.map(({ name, image, backgroundColor, id }) => ({
			name,
			filepath: image,
			backgroundColor,
			id,
		}));
};

export const getMappedRoomList = (rooms, roomId) => {
	return rooms.map((room) => {
		return {
			...room,
			image: room.id !== -1 && room.filepath,
			backgroundColor: room.backgroundColor,
			selected: +room.id === +roomId,
			disabled: false,
		};
	});
};

// SORT AND FILTERS
export const filterRoomsByName = (rooms, filter: string) => {
	const currentFilter = filter.toLowerCase();

	return rooms.filter((room) => {
		return room.name.toLowerCase().includes(currentFilter);
	});
};

export const filterDevicesByDeviceName = (array, filter: string, prefix?: string) => {
	return array.filter((item) => {
		return filterDeviceByName(prefix ? prefix + item.name : item.name, filter.toLowerCase());
	});
};

export const filterScenesByName = (scenes, filter: string) => {
	const currentFilter = filter.toLowerCase();

	return scenes.filter((scene) => {
		return scene._meta.name.toLowerCase().includes(currentFilter);
	});
};

export const filterScenesInitial = (e, ind) => {
	if (
		!e.hasOwnProperty('tschem') &&
		!e.hasOwnProperty('_gui_function') &&
		e._meta?.sceneType !== 'flow'
	) {
		//delete all no needed data (it must be on clean up phase after get list from PHP)
		delete e.position;
		delete e.image;
		delete e.name;

		return e;
	}
};

export const sortBy = (x, y, prop) => {
	return x[prop] - y[prop];
};

export const sortByPosition = (x: { position: number }, y: { position: number }) => {
	return x.position - y.position;
};

export const filterDevice = (device, filter: string): boolean => {
	// filter is passed by lowercase so all device props need to be with lowercase as well
	return (
		device.name.toLowerCase().includes(filter) ||
		device.id.toLowerCase().includes(filter) ||
		device?.type?.toLowerCase()?.includes(filter)
	);
};

export const filterGroup = (group, filter: string): boolean => {
	// filter is passed by lowercase so group name needs to be with lowercase as well
	return group.name.toLowerCase().includes(filter);
};

export const filterScene = (scene, filter: string): boolean => {
	// filter is passed by lowercase so scene name needs to be with lowercase as well
	return scene._meta.name.toLowerCase().includes(filter);
};

export const filterAlarm = (alarm, filter: string): boolean => {
	// filter is passed by lowercase so scene name needs to be with lowercase as well
	return alarm._meta.name.toLowerCase().includes(filter);
};

export const filterThermostat = (thermostat, filter: string): boolean => {
	// filter is passed by lowercase so thermostat name needs to be with lowercase as well
	return thermostat._meta.name.toLowerCase().includes(filter);
};

export const filterRoom = (room, filter: string): boolean => {
	// filter is passed by lowercase so room name needs to be with lowercase as well
	return room.name.toLowerCase().includes(filter);
};

export const sortListByPossition = (list: any) => {
	return list.sort((x: any, y: any) => {
		return sortBy(x, y, 'position');
	});
};

export const filterDeviceByName = (deviceName: string, filter: string): boolean => {
	return deviceName.toLowerCase().includes(filter);
};

export const filterDeviceById = (deviceId: string, filter: string): boolean => {
	return deviceId.toLowerCase().includes(filter);
};

export const getAllDevicesByRoomID = (roomID) => {
	const _devices: Devices = get(devices);
	return Object.values(_devices).filter((d) => {
		return d.room_id == roomID;
	});
};

export const checkIsEventLogTab = (tab) => {
	return tab.route === '/event_log';
};

export const checkIsCameraMonitoringTab = (tab) => {
	return tab.route === '/camera_monitoring';
};

export const checkIsNonDashboardTab = (tab) => {
	return checkIsEventLogTab(tab) || checkIsCameraMonitoringTab(tab);
};

export const getIsActivityLogVisible = (profileData) =>
	profileData?.isActivityLogVisible?.value !== undefined
		? !!profileData.isActivityLogVisible.value
		: true;

export const getIsCameraMonitoringVisible = (profileData) =>
	profileData?.isCameraMonitoringVisible?.value !== undefined
		? !!profileData.isCameraMonitoringVisible.value
		: true;

export const isDashTabVisible = (
	tab,
	isActivityLogVisible: boolean,
	isCameraMonitoringVisible: boolean,
) =>
	(!checkIsEventLogTab(tab) || isActivityLogVisible) &&
	(!checkIsCameraMonitoringTab(tab) || isCameraMonitoringVisible);

export const groupBy = (arr: any[], property: string, groupCallback: (group) => any[] = null) => {
	const groupedData = arr.reduce((acc, cur) => {
		acc[cur[property]] = [...(acc[cur[property]] || []), cur];
		return acc;
	}, {});

	let result = [];

	for (const key in groupedData) {
		if (Object.prototype.hasOwnProperty.call(groupedData, key)) {
			const currentGroupData = groupedData[key];
			if (groupCallback) {
				const updatedGoup = groupCallback(currentGroupData);
				result = [...result, ...updatedGoup];
			} else {
				result = [...result, ...currentGroupData];
			}
		}
	}

	return result;
};
/**
 * Resolves Shelly model type for gateway-paired BTHome virtual devices (`bthome-device`).
 */
export const getBthomeShellyModelType = (device: Device): string | undefined => {
	if (device?.type !== 'bthome-device') {
		return undefined;
	}

	const parentId = real_id(device.id);
	const component = getComponentByDevice(device);
	if (!component) {
		return undefined;
	}

	const settings = devices_settings.getByRealId(parentId);
	const templateType = settings?.[component]?.meta?.template?.type;
	if (typeof templateType === 'string') {
		return templateType;
	}

	const status = devices_status.getByRealId(parentId);
	const modelId = status?.[component]?._attrs?.model_id;
	if (typeof modelId !== 'number') {
		return undefined;
	}

	return bthomeDeviceToModel.find((model) => model.id === modelId)?.type;
};

/** Motion, door/window, presence, and Zigbee BTHome sensors eligible for alarm zones. */
export const isAlarmZoneSensorDevice = (device: Device): boolean => {
	const modelType = getBthomeShellyModelType(device) || device?.type;
	if (!modelType) {
		return false;
	}

	return isMotion(modelType) || isDoorWindow(modelType);
};

/** used in alarm zones only */
export const filterSensors = (devices) => {
	let filtered = [];
	devices.forEach((d) => {
		if (
			!d.shared &&
			(isTestUser() || d.room_id !== HiddenDevicesRoomID) &&
			d.room_id != `${SharedRecentlyDevicesRoomID}`
		) {
			if (isAlarmZoneSensorDevice(d)) {
				filtered.push(d);
			} else if (is_gen_2(d)) {
				filtered = getPeripheralDigitalInputDevices(d, filtered);
			}
		}
	});
	return filtered;
};

// DND helpers

export const dndHandleClick = (e: MouseEvent | TouchEvent | CustomEvent, edit: boolean) => {
	if (!edit) return;
	if (e.cancelable) e.preventDefault();

	const button = e.currentTarget as HTMLElement;
	button.classList.toggle('dnd-handle-drag');
};

export const onStartDrag = (
	e: MouseEvent | TouchEvent | CustomEvent,
	edit: boolean,
	handleStartDrag: () => void,
	stopDndHandleClick?: boolean,
) => {
	if (!stopDndHandleClick) {
		dndHandleClick(e, edit);
	}
	handleStartDrag();
};

export const onEndDrag = (
	e: MouseEvent | TouchEvent | CustomEvent,
	edit: boolean,
	handleEndDrag: () => void,
	stopDndHandleClick?: boolean,
) => {
	if (!stopDndHandleClick) {
		dndHandleClick(e, edit);
	}
	handleEndDrag();
};

export const setWizardSteps = (stepsNamesList: string[], activeIndex = 0) => {
	return stepsNamesList.map((t, i) => {
		return {
			title: t,
			active: i === activeIndex ? true : false,
			disabled: false,
			showMoreDots: false,
			stepNumber: i + 1,
			tooltipTitle: `Step ${i + 1}`,
			activeIndex: i,
		};
	});
};

export const setWizardStepsEXtended = (stepsList: any[], activeIndex = 0) => {
	return stepsList.map(({ title, scroll }, i) => {
		return {
			title,
			active: i === activeIndex ? true : false,
			disabled: false,
			showMoreDots: false,
			stepNumber: i + 1,
			tooltipTitle: `Step ${i + 1}`,
			activeIndex: i,
			scroll,
		};
	});
};

export const updateWizardStepOnWizardItem = (
	index: number,
	wizardItems: wizardItem[],
	activeWizardStep: wizardItem,
) => {
	let currentActiveStep = { ...activeWizardStep };
	const updatedWizardItems = wizardItems.map((item, i) => {
		if (i === index) {
			currentActiveStep = {
				...item,
				activeIndex: i,
			};
			return {
				...item,
				active: true,
			};
		}

		return {
			...item,
			active: false,
		};
	});

	return {
		wizardItems: updatedWizardItems,
		activeWizardStep: currentActiveStep,
	};
};

export const updateWizardStepOnPrevNext = (
	type: 'prev' | 'next',
	wizardItems: wizardItem[],
	activeWizardStep: wizardItem,
) => {
	let currentActiveStep = { ...activeWizardStep };
	const nextStepIndex = currentActiveStep.activeIndex + 1;
	const prevStepIndex = currentActiveStep.activeIndex - 1;
	const updatedWizardItems = [...wizardItems].map((item, i) => {
		if (i === nextStepIndex && type === 'next') {
			currentActiveStep = {
				...item,
				activeIndex: i,
			};
			return {
				...item,
				active: true,
			};
		}

		if (i === prevStepIndex && type === 'prev') {
			currentActiveStep = {
				...item,
				activeIndex: i,
			};
			return {
				...item,
				active: true,
			};
		}

		return {
			...item,
			active: false,
		};
	});

	return {
		wizardItems: updatedWizardItems,
		activeWizardStep: currentActiveStep,
	};
};

export const resetWizardItems = (wizardItems: wizardItem[]) => {
	return wizardItems.map((item, i) => {
		if (i === 0)
			return {
				...item,
				active: true,
			};

		return {
			...item,
			active: false,
		};
	});
};

export const scrollToElement = (
	element: HTMLElement,
	wizardItemsContainerRef: HTMLElement,
	activeWizardStepIndex: number,
	wizardStepIndex: number,
) => {
	if (!element) return;
	const removeContainerEventListener = () => {
		wizardItemsContainerRef.removeEventListener('transitionend', scrollToCard);
	};

	const scrollToCard = (e) => {
		// This stop any transition bubling
		e.stopPropagation();
		if (activeWizardStepIndex === wizardStepIndex && e.target === e.currentTarget) {
			element.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});

			// Clear event listener
			return wizardItemsContainerRef.removeEventListener('transitionend', scrollToCard);
		}
		// Clear event listener
		return removeContainerEventListener();
	};

	// Used event listener to prevent using timeout
	wizardItemsContainerRef.addEventListener('transitionend', scrollToCard);

	return removeContainerEventListener;
};

export const scrollToElementInConditionalStep = (element: HTMLElement, condition: boolean) => {
	if (!element || !condition) return;

	element.scrollIntoView({
		behavior: 'smooth',
		block: 'nearest',
	});
};

//end Wizard helpers

// start Dashboard edit helpers

function fixCorruptedDashabordData(currentDashboard) {
	if (typeof currentDashboard[''] !== undefined) {
		delete currentDashboard[''];
	}
	if (typeof currentDashboard['text'] !== undefined) {
		delete currentDashboard['text'];
	}

	return currentDashboard;
}

export type DashboardVisual = {
	icon?: string;
	backgroundColor?: string;
	/** @deprecated Legacy single-field avatar image; prefer icon + backgroundColor. */
	image?: string;
};

export async function addDashboard(name: string, visual?: DashboardVisual | null) {
	const url = '/interface/dash/update';

	let lastPosition = -1;

	const dashInLastPosition: any = Object.values(get(dashboards)).reduce(
		(prev: any, current: any) => (prev.position > current.position ? prev : current),
		{},
	);

	if (dashInLastPosition.position || +dashInLastPosition.position === 0) {
		lastPosition = +dashInLastPosition.position + 1;
	} else {
		lastPosition += 1;
	}

	const newTab: Record<string, unknown> = {
		name: name,
		rooms: {},
		groups: {},
		devices: {},
		scenes: {},
		image: '/[image_url]',
		position: lastPosition,
		icon: visual?.icon?.trim() || 'fat fa-objects-column',
	};

	if (visual?.backgroundColor?.trim()) {
		newTab.backgroundColor = visual.backgroundColor.trim();
	}

	if (visual?.image?.trim() && visual.image.trim() !== '/[image_url]') {
		newTab.image = visual.image.trim();
	}

	const res: DashboardUpdateResponse = await postReq(url, {
		data: JSON.stringify(newTab),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[res.data.dash_id]: res.data.dash_data,
		});
		return res.data.dash_id;
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function deleteDashboard(dashboardId) {
	const url = '/interface/dash/delete';

	const res: DashboardDeleteResponse = await postReq(url, {
		id: dashboardId,
	});

	if (res.isok) {
		dashboards.remove(dashboardId);
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function requestReorderDashboards(editedList) {
	const url = '/interface/dash/update';

	const dashboardsCopy = { ...get(dashboards) };

	for (const key in editedList) {
		const element = editedList[key];

		// ignore "Activity Log" and "Camera Monitoring"
		if (element.id !== -10 && element.id !== -11) {
			dashboardsCopy[key].position = element.position;

			// TODO Temporary sending for each
			await postReq(url, {
				id: key,
				data: JSON.stringify({
					...dashboardsCopy[key],
					position: element.position,
				}),
			});
		}
	}

	dashboards.update({
		...dashboardsCopy,
	});
}

// export async function requestEditDashboard(dashboardId) {
// 	const url = '/interface/dash/update';

// 	const dashboardCopy = { ...get(dashboards)[dashboardId] };

// 			dashboardCopy.position = element.position;

// 			// TODO Temporary sending for each
// 			await postReq(url, {
// 				id: key,
// 				data: JSON.stringify({
// 					...dashboardsCopy[key],
// 					position: element.position
// 				})
// 			});

// 	dashboards.update({
// 		...dashboardCopy
// 	});
// }

export async function requestChangeDashboardName(dashboardId, newName, visual?: DashboardVisual | null) {
	const url = '/interface/dash/update';

	const updatedDashboard = {
		...get(dashboards)[dashboardId],
		name: newName,
	};

	if (visual) {
		// icon + backgroundColor coexist (page bg + tab RectangularAvatar plate).
		// Clear legacy image avatar when updating the new visual model.
		delete updatedDashboard.image;

		if (visual.icon !== undefined) {
			updatedDashboard.icon = visual.icon?.trim() || 'fat fa-objects-column';
		}

		if (visual.backgroundColor !== undefined) {
			const token = visual.backgroundColor?.trim();
			if (token) {
				updatedDashboard.backgroundColor = token;
			} else {
				delete updatedDashboard.backgroundColor;
			}
		}

		if (visual.image?.trim() && visual.image.trim() !== '/[image_url]') {
			updatedDashboard.image = visual.image.trim();
		}
	}

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function requestReorderDashboardItems(dashboardId, editedList) {
	const url = '/interface/dash/update';

	const types = {
		rooms: {},
		groups: {},
		devices: {},
		scenes: {},
		alarms: {},
		temp_schemes: {},
		weather: {},
	};

	for (const key in editedList) {
		const element = editedList[key];

		types[element._type][element.id] = {
			position: element.position,
		};
	}

	const updatedDashboard = {
		...get(dashboards)[dashboardId],
		...types,
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function requestAddDashboardItem(dashboardId, itemType, itemId, itemPosition?) {
	const url = '/interface/dash/update';
	const types = ['groups', 'devices', 'scenes', 'rooms', 'alarms', 'temp_schemes', 'weather'];
	let lastPosition = -1;

	let currentDashboard = { ...get(dashboards)[dashboardId] };

	currentDashboard = fixCorruptedDashabordData(currentDashboard);

	if (itemPosition !== undefined) {
		lastPosition = itemPosition;
	} else {
		for (const key in types) {
			const type = types[key];

			for (const key in currentDashboard[type]) {
				const itemPosition = currentDashboard[type][key].position;

				if (itemPosition > lastPosition) {
					lastPosition = itemPosition;
				}
			}
		}

		lastPosition += 1;
	}

	// console.log('itemType: ', itemType);
	// console.log('currentDashboard[itemType]: ', currentDashboard[itemType]);
	if (currentDashboard[itemType] !== undefined) {
		currentDashboard[itemType][itemId] = {
			position: lastPosition,
		};
	} else {
		currentDashboard = {
			...currentDashboard,
			[itemType]: {
				...currentDashboard[itemType],
				[itemId]: { position: lastPosition },
			},
		};
	}

	const updatedDashboard = {
		...currentDashboard,
		[itemType]: {
			...currentDashboard[itemType],
		},
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function requestDeleteDashboardItem(dashboardId, itemType, itemId) {
	const url = '/interface/dash/update';

	let currentDashboard = { ...get(dashboards)[dashboardId] };

	currentDashboard = fixCorruptedDashabordData(currentDashboard);

	const currentItemType = { ...currentDashboard[itemType] };

	delete currentItemType[itemId];

	if (typeof currentItemType[''] !== undefined) {
		delete currentItemType[''];
	}
	if (typeof currentItemType['text'] !== undefined) {
		delete currentItemType['text'];
	}

	const updatedDashboard = {
		...currentDashboard,
		[itemType]: {
			...currentItemType,
		},
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function requestChangeDashboardSettings(dashboardId, settings) {
	const url = '/interface/dash/update';

	const updatedDashboard = {
		...get(dashboards)[dashboardId],
		settings: settings,
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

// end Dashboard edit helpers

// Redirect helpers
export const navigateToUrl = (url: string, tab = true) => {
	if (get(app)) {
		if (get(os) === 'IOS') {
			open_external_link(url);
		} else {
			open_external_link_android(url);
		}
	} else {
		if (tab) {
			window.open(url, '_blank');
		} else {
			window.location.href = url;
		}
	}
};

export const navigateToLoqedLinking = (url: string, tab = true) => {
	if (get(app)) {
		if (get(os) === 'IOS') {
			open_loqed_linking_ios(url);
		} else {
			open_external_link_android(url);
		}
	} else {
		if (tab) {
			window.open(url, '_blank');
		} else {
			window.location.href = url;
		}
	}
};

export const navigateToShop = (url: string = '') => {
	const defaultUrl = `https://www.shelly.cloud/en/products/shop?origin=shelly_app&shelly_user=${get(user_data).email}#43e73c569e094a20aabe8d83a5f2426d`;
	const currentUrl = url || defaultUrl;
	navigateToUrl(currentUrl);
};

const CURY_BASE_URL = 'https://cury.shelly.cloud/';
const CURY_THIRD_PARTY_LOGIN_PATH = 'third-party-integrations-login';

/**
 * Navigate to Cury third-party integrations page. When the user is logged in, opens the
 * third-party-integrations-login URL with the auth token so Cury can link the account without
 * asking for credentials. On mobile app opens in the in-app WebView; on web opens in same or new tab.
 */
export const navigateToCuryThirdPartyLogin = (extraQueryString = '', tab = true) => {
	const token = get(user_data)?.token;

	const url =
		token !== undefined && token !== ''
			? `${CURY_BASE_URL}${CURY_THIRD_PARTY_LOGIN_PATH}?token=${encodeURIComponent(token)}${extraQueryString ? `&${extraQueryString.replace(/^&/, '')}` : ''}`
			: `${CURY_BASE_URL}${extraQueryString ? `?${extraQueryString.replace(/^\?/, '')}` : ''}`;

	navigateToUrl(url, tab);
};

// Scroll to additional content helpers
export const scrollToAdditionalContent = (elementRef: HTMLElement, containerRef: HTMLElement) => {
	const spacingFromTopContaier = 64;

	if (elementRef && containerRef) {
		const elementRect = elementRef.getBoundingClientRect();
		const containerRect = containerRef.getBoundingClientRect();
		const offsetPosition =
			elementRect.top - containerRect.top + containerRef.scrollTop - spacingFromTopContaier;

		containerRef.scrollTo({
			top: offsetPosition,
			behavior: 'smooth',
		});
	}
};

export const scrollToElementInWizard = (
	wizardContainerRef: HTMLElement,
	showedConditionalElRef: HTMLElement,
	scrollContainerRef: HTMLElement,
) => {
	if (wizardContainerRef && showedConditionalElRef) {
		const scrollToVisibleItem = (e) => {
			if (e.propertyName === 'height') {
				scrollToAdditionalContent(showedConditionalElRef, scrollContainerRef);

				return wizardContainerRef.removeEventListener('transitionend', scrollToVisibleItem);
			}
		};

		wizardContainerRef.addEventListener('transitionend', scrollToVisibleItem);
	}
};

/**
 * Function to get device(channel) mode for XMOD based on number of inputs and outputs
 * @param ni Number of inputs
 * @param no Number of outputs
 * @returns Device mode
 */
const getModuleDeviceMode = (ni: number, no: number) => {
	if (ni > 0 && no > 0) {
		return 'relay';
	} else if (no > 0 && ni <= 0) {
		return 'output_reader';
	} else if (ni > 0 && no <= 0) {
		return 'inputs_reader';
	} else if (ni == 0 && no == 0) {
		return 'shell';
	}
};

export const extractDevicesFromAlarm = (alarm: IAlarmZone) => {
	let rrValues = [];
	let sensors = [];

	if (alarm?.if?.and) {
		sensors = [...alarm.if.and];
	} else if (alarm?.if?.or) {
		sensors = [...alarm.if.or];
	}

	let sensorsList = [];

	for (let devSensor of sensors) {
		if (devSensor?.or) {
			if (!devSensor.or[0]?.indailyint) {
				sensorsList = [...devSensor.or];
			}
		} else {
			sensorsList.push(devSensor);
		}
	}

	for (let sensor of sensorsList) {
		if (sensor.rr) {
			rrValues.push(sensor.rr);
		} else if (sensor.eq && Array.isArray(sensor.eq) && sensor.eq[0]?.rr) {
			rrValues.push(sensor.eq[0].rr);
		}
	}

	return rrValues;
};

/** Index of the `if.and` branch that holds the device `or` list (excludes active-time `indailyint` nodes). */
export function foundedAlarmDevicesAndIndex(andArr: any[] | undefined): number {
	if (!andArr?.length) return 0;
	for (let i = 0; i < andArr.length; i++) {
		if (andArr[i].or?.length > 0) {
			for (let x = 0; x < andArr[i].or.length; x++) {
				const el = andArr[i].or[x];
				if (el && !el.hasOwnProperty('indailyint')) {
					return i;
				}
			}
		} else {
			return i;
		}
	}
	return 0;
}

/** Mutable list of device condition objects `{ eq: [{ rr }] }` on the alarm (`if.or` or nested `if.and[].or`). */
export function getAlarmDeviceConditions(alarm: IAlarmZone | undefined): any[] {
	if (!alarm?.if) return [];
	if (alarm.if.or?.length) {
		return alarm.if.or;
	}
	if (alarm.if.and?.length) {
		const i = foundedAlarmDevicesAndIndex(alarm.if.and);
		return alarm.if.and[i]?.or ?? [];
	}
	return [];
}

export const checkIsBreachedAlarmSensor = (device: Device, component?: string) => {
	// In alarms only used door/window and motion sensors
	const deviceStatus = devices_status.getByRealId(device.id);
	const deviceType = getBthomeShellyModelType(device) || device.type;
	let isBreached = false;
	if (isDoorWindow(deviceType)) {
		// Check for breached door/window sensor
		isBreached = deviceStatus?.sensor?.state === 'open' || !!deviceStatus?.['window:0']?.open;
	} else if (isShellyPresence(deviceType)) {
		isBreached = !!(
			component &&
			/^presencezone:\d+$/.test(component) &&
			deviceStatus?.[component]?.value
		);
	} else if (isMotion(deviceType)) {
		// Check for breached motion sensor
		isBreached = !!deviceStatus?.['motion:0']?.motion || !!deviceStatus?.sensor?.motion;
	}

	return isBreached;
};

export type AlarmDeviceStateType = {
	id: string;
	offline: boolean;
	breached: boolean;
	checked: boolean;
	deletedDevice: boolean;
	alarmList?: IAlarmZone[];
};

export interface AlarmGroupedDevices {
	offline: AlarmDeviceStateType[];
	breached: AlarmDeviceStateType[];
	checked: AlarmDeviceStateType[];
	deletedDevice: AlarmDeviceStateType[];
}

export type ColorVariant = 'success' | 'warning' | 'danger' | 'muted';
export interface AlarmDelayModalType {
	[key: string]: boolean;
}
export interface AlarmDelayTimerType {
	[key: string]: number | undefined;
}

export let checkIsAllAlarmsArmed = (alarms: IAlarmZone[], checkTimeTrigger = true) => {
	if (!checkTimeTrigger) {
		return alarms.every((alarm) => alarm._enabled);
	}

	return alarms.every((alarm) => alarm._enabled && !alarm?._meta?._time_trigger);
};

export const mapDevicesByAlarm = (newList: AlarmDeviceStateType[], oldList: AlarmDeviceStateType[]) => {
	let mappedList = [...oldList];
	newList.forEach((newDev) => {
		const devIndex = mappedList.findIndex((dev) => dev.id === newDev.id);
		if (devIndex === -1) {
			mappedList = [...mappedList, newDev];
		} else {
			mappedList[devIndex] = {
				...mappedList[devIndex],
				alarmList: [...mappedList[devIndex].alarmList, ...newDev.alarmList],
			};
		}
	});

	return mappedList;
};

export const getAllAlarmsGroupedDevices = (alarms: IAlarmZone[]) => {
	let groupedDevices = {
		offline: [],
		breached: [],
		checked: [],
		deletedDevice: [],
	};

	alarms.forEach((alarm) => {
		const currentAlarmGroupedDev = getAlarmGroupedDevices(alarm);

		groupedDevices = {
			offline: [...mapDevicesByAlarm(currentAlarmGroupedDev.offline, groupedDevices.offline)],
			breached: [...mapDevicesByAlarm(currentAlarmGroupedDev.breached, groupedDevices.breached)],
			checked: [...mapDevicesByAlarm(currentAlarmGroupedDev.checked, groupedDevices.checked)],
			deletedDevice: [
				...mapDevicesByAlarm(currentAlarmGroupedDev.deletedDevice, groupedDevices.deletedDevice),
			],
		};
	});

	return groupedDevices;
};

export const getAlarmDeviceState = (deviceRR: string): AlarmDeviceStateType => {
	const parsed = parseAlarmDeviceRr(deviceRR);
	const splitID = parsed?.decimal;
	const device = splitID ? get(devices)[shellyId16(splitID)] : undefined;

	const isDeletedDevice = !device;
	if (isDeletedDevice) {
		return {
			id: splitID,
			offline: false,
			breached: false,
			checked: false,
			deletedDevice: isDeletedDevice,
		};
	}

	const deviceStatus = devices_status.getByRealId(device.id);
	let isOffline = deviceStatus?.online === 0 || !device?.cloud_online;
	const component = parsed?.component;
	let isBreached = checkIsBreachedAlarmSensor(device, component);
	let alarmDeviceID = device?.id;

	if (device && component) {
		alarmDeviceID =
			getPeripheralDigitalInputDevices(device).find((f) => f.ext_sen_id == component)
				?.peripheralDeviceID || device.id;
	}

	return {
		id: alarmDeviceID,
		offline: isOffline,
		breached: isBreached && !isOffline,
		checked: !isOffline && !isBreached,
		deletedDevice: isDeletedDevice,
	};
};

/**
 * One row per physical device in alarm overview / breached lists.
 * Presence alarms often have multiple `if.or` entries per device (one per zone); those
 * resolve to the same `id` from {@link getAlarmDeviceState} and must be merged so
 * offline / breached / checked reflects the worst case across conditions.
 */
const dedupeAlarmDeviceStatesById = (states: AlarmDeviceStateType[]): AlarmDeviceStateType[] => {
	const byId = new Map<string, AlarmDeviceStateType[]>();
	for (const s of states) {
		const key = String(s.id);
		const list = byId.get(key) ?? [];
		list.push(s);
		byId.set(key, list);
	}

	const merged: AlarmDeviceStateType[] = [];
	for (const [, group] of byId) {
		if (group.length === 1) {
			merged.push(group[0]);
			continue;
		}

		const offline = group.some((g) => g.offline);
		const breached = !offline && group.some((g) => g.breached);
		const checked = !offline && !breached && group.some((g) => g.checked);
		const deletedDevice = group.some((g) => g.deletedDevice);

		const alarmList: IAlarmZone[] = [];
		const seenAlarm = new Set<string | number>();
		for (const g of group) {
			for (const a of g.alarmList ?? []) {
				const k = a?._id ?? a?.id;
				if (k == null || seenAlarm.has(k)) continue;
				seenAlarm.add(k);
				alarmList.push(a);
			}
		}

		merged.push({
			id: group[0].id,
			offline,
			breached,
			checked,
			deletedDevice,
			alarmList: alarmList.length ? alarmList : group[0].alarmList,
		});
	}

	return merged;
};

export const getAlarmGroupedDevices = (alarm: IAlarmZone) => {
	const alarmDevicesID = extractDevicesFromAlarm(alarm);
	let allDeviceStates: AlarmDeviceStateType[] = [];
	alarmDevicesID.forEach((id) => {
		const deviceStates = getAlarmDeviceState(id);
		allDeviceStates = [...allDeviceStates, { ...deviceStates, alarmList: [alarm] }];
	});

	const deduped = dedupeAlarmDeviceStatesById(allDeviceStates);

	return {
		offline: deduped.filter((s) => s.offline),
		breached: deduped.filter((s) => s.breached),
		checked: deduped.filter((s) => s.checked),
		deletedDevice: deduped.filter((s) => s.deletedDevice),
	};
};

/** @internal shared helper – adds one device to the mutable roomsWithSensors array */
const _addDeviceToRooms = (device: any, roomsWithSensors: any[], $rooms: any) => {
	const idx = roomsWithSensors.findIndex((r) => Number(r.id) === Number(device.room_id));
	if (idx >= 0) {
		roomsWithSensors[idx].devices.push(device);
	} else {
		roomsWithSensors.push({
			id: device.room_id,
			name: $rooms[device.room_id]?.name,
			devices: [device],
		});
	}
};

/**
 * Builds roomsWithSensors from alarm conditions.
 * Presence devices are deduplicated (one card per device regardless of how many
 * zone conditions exist) and each presence device entry gets an `_alarmZoneLabel`
 * string listing the configured zone names (resolved from devices_settings).
 *
 * @returns roomsWithSensors, missingDevices, and hasPresence — a flag that is true
 *   when at least one presence device exists in the alarm, used to gate zone label
 *   rendering in the drawer and side panel.
 */
export const buildAlarmRoomsWithSensors = (
	alarm: IAlarmZone,
	$devices: any,
	$rooms: any,
): { roomsWithSensors: any[]; missingDevices: any[]; hasPresence: boolean } => {
	const $settings = get(devices_settings);

	let sensors: any[] = [];
	if (alarm?.if?.and) sensors = [...alarm.if.and];
	else if (alarm?.if?.or) sensors = [...alarm.if.or];

	let sensorsList: any[] = [];
	for (const devSensor of sensors) {
		if (devSensor?.or) {
			if (!devSensor?.or?.[0]?.indailyint) sensorsList = [...devSensor.or];
		} else {
			sensorsList.push(devSensor);
		}
	}

	const roomsWithSensors: any[] = [];
	const missingDevices: any[] = [];
	// deviceId → collected zone components (prsn_st4NNN)
	const presenceZoneMap = new Map<string, string[]>();

	for (const _sensor of sensorsList) {
		const rr = _sensor?.eq ? _sensor?.eq?.[0]?.rr : _sensor?.or?.[0]?.eq?.[0]?.rr;
		const parsed = parseAlarmDeviceRr(rr);
		const decimal = parsed?.decimal;
		const component = parsed?.component ?? '';

		let device = decimal ? $devices[shellyId16(decimal)] : undefined;
		if (device) {
			const addonDevice =
				getPeripheralDigitalInputDevices(device).find((f) => f.ext_sen_id == component) ||
				device;
			const isPresenceZone = /^prsn_st4\d+$/.test(component);

			if (isPresenceZone && isShellyPresence(device.type)) {
				// Group: add the device to rooms only on the first zone condition
				if (!presenceZoneMap.has(device.id)) {
					presenceZoneMap.set(device.id, []);
					_addDeviceToRooms(device, roomsWithSensors, $rooms);
				}
				presenceZoneMap.get(device.id)!.push(component);
			} else {
				_addDeviceToRooms(addonDevice, roomsWithSensors, $rooms);
			}
		} else if (decimal) {
			missingDevices.push({ id: decimal });
		}
	}

	// Resolve zone names and attach as _alarmZoneLabel on the device entry
	for (const [deviceId, zoneComponents] of presenceZoneMap) {
		const devSettings = $settings?.[real_id(deviceId)];

		const zoneNames = zoneComponents
			.map((comp) => {
				const m = comp.match(/^prsn_st4(\d+)$/);
				if (!m) return null;
				const deviceZoneId = String(parseInt(m[1]) - 1);
				const name = devSettings?.[`presencezone:${deviceZoneId}`]?.name;
				return (
					name ||
					get(format)('label_zone_number', {
						default: 'Zone {n}',
						values: { n: deviceZoneId },
					})
				);
			})
			.filter(Boolean);

		for (const room of roomsWithSensors) {
			const entry = room.devices.find((d: any) => d.id === deviceId);
			if (entry) {
				entry._alarmZoneLabel = zoneNames.join(', ');
				break;
			}
		}
	}

	return { roomsWithSensors, missingDevices, hasPresence: presenceZoneMap.size > 0 };
};

export const formatSecsToTimeString = (seconds: number) => {
	const time = convertSecondsToDDHHMMSS(seconds);
	const daysPart = time.days === '00' ? '' : `${time.days}:`;
	return `${daysPart}${time.hours}:${time.minutes}:${time.seconds}`;
};

export const sortRoomsWithMissingDevices = (filteredDevicesByRoom) => {
	const sortedRooms = Object.entries(filteredDevicesByRoom)
		.sort(([keyA], [keyB]) => {
			if (keyA === 'missing_devices') return -1;
			if (keyB === 'missing_devices') return 1;
			return Number(keyA) - Number(keyB);
		})
		.map(([_, value]) => value);

	return Object.values(sortedRooms);
};

export const generateWhenAndDoMissingDevices = (missingDevice: {
	title: string;
	value: string | undefined;
	id: string;
}): MissingDevicesGroup | null => {
	if (!missingDevice?.title) return null;

	const roomName = _format('missing_devices_label', {
		default: 'Missing Devices',
	});

	return {
		cards: [
			{
				title: missingDevice.id,
				name: missingDevice.id,
				value: missingDevice.id,
				id: missingDevice.id,
				roomID: 'missing_devices',
				roomName,
				selected: true,
				icon: 'text-warning fas fa-warning',
			},
		],
		roomName,
		roomID: 'missing_devices',
	};
};

export const checkForMissingDevice = (devicesArr) => {
	const missingIds = devicesArr?.filter(
		(id) => !Object.values(get(devices)).some((device) => device.id === id),
	);

	return missingIds;
};
